<template>
  <div class="cases-page">
    <LazyBg :src="pageBg" eager />
    <div class="cases-inner container">
      <nav class="cases-tabs" role="tablist" aria-label="切换案例">
        <div class="cases-tab-row">
          <button v-for="(c, i) in cases" :id="`cases-tab-${c.id}`" :key="c.id" type="button" role="tab"
            class="cases-tab" :class="{ 'is-active': i === activeIndex }" :aria-selected="i === activeIndex"
            :aria-controls="`cases-panel-${c.id}`" @click="selectCase(i)">
            <span class="cases-tab-label" :title="c.tabLabel">{{ c.tabLabel }}</span>

          </button>

        </div>
      </nav>

      <Transition name="cases-panel" mode="out-in">
        <article v-if="activeCase" :id="`cases-panel-${activeCase.id}`" :key="activeCase.id" class="cases-body"
          role="tabpanel" :aria-labelledby="`cases-tab-${activeCase.id}`">
          <div class="cases-content">
            <h1 class="cases-title">{{ activeCase.title }}</h1>
            <p class="cases-desc">{{ activeCase.description }}</p>
            <ul v-if="activeCase.tags?.length" class="cases-tags" role="list">
              <li v-for="tag in activeCase.tags" :key="tag" class="cases-tag">{{ tag }}</li>
            </ul>
          </div>

          <div v-if="displayShots.length" class="cases-visual">
            <div class="cases-carousel">
              <Transition name="cases-shot" mode="out-in">
                <img :key="`${activeCase.id}-${shotIndex}`" class="cases-hero-img" :src="displayShots[shotIndex]"
                  :alt="`${activeCase.title} 场景图 ${shotIndex + 1}`" loading="lazy" />
              </Transition>
              <div v-if="displayShots.length > 1" class="cases-dots" role="tablist" aria-label="案例配图">
                <button v-for="(_, i) in displayShots" :key="i" type="button" role="tab" class="cases-dot"
                  :class="{ 'is-active': i === shotIndex }" :aria-selected="i === shotIndex"
                  :aria-label="`第 ${i + 1} 张`" @click="shotIndex = i" />
              </div>
              <div class="cases-shadow "></div>
            </div>
          </div>
        </article>
      </Transition>

      <p v-if="!cases.length" class="cases-empty">暂无成功案例</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import pageBg from '@/assets/caseBg.jpg';
import LazyBg from '@/components/LazyBg.vue';
import { computed, ref, watch } from 'vue';
import { useSeo } from '@/composables/useSeo';
import { useFrontCms } from '@/composables/useFrontCms';
import { mapSuccessCasesToShowcases, type CaseShowcaseView } from '@/utils/frontCmsMappers';

const { successCases } = useFrontCms('successCases');

const SHOT_SERVER =
  'https://images.unsplash.com/photo-1558494940-ef010cbdcc15?auto=format&fit=crop&w=1200&q=80';

const FALLBACK_CASES: CaseShowcaseView[] = [
  {
    id: 'wenzhou-hpc',
    tabLabel: '温州市自然资源和规划局HPC集群建设',
    title: '温州市自然资源和规划局HPC集群建设',
    description:
      '为温州市自然资源和规划局建设高性能计算（HPC）集群，支撑国土空间规划、遥感影像处理与三维建模等核心业务。项目涵盖服务器部署、网络架构搭建与监控平台集成，实现算力统一调度与资源可视化运维，满足政务外网环境下的安全合规要求。',
    shots: [SHOT_SERVER],
    backgroundUrl: '',
    tags: ['服务器部署', '网络架构搭建', '监控平台集成']
  },
  {
    id: 'comac-hpc',
    tabLabel: '商飞软件高性能计算平台',
    title: '商飞软件高性能计算平台',
    description:
      '面向航空研发场景打造高性能计算与仿真平台，整合批处理调度、作业队列管理与资源配额策略，支撑 CFD、结构仿真等大规模算力任务，显著缩短设计验证周期并提升集群利用率。',
    shots: [SHOT_SERVER],
    backgroundUrl: '',
    tags: ['算力调度', '仿真计算', '国产化适配']
  },
  {
    id: 'mthreads',
    tabLabel: '摩尔线程AI算力集群',
    title: '摩尔线程AI算力集群',
    description:
      '基于国产化 GPU 构建 AI 训练与推理算力集群，完成硬件选型、集群组网、容器化调度与监控告警体系建设，为模型训练与推理服务提供稳定、可扩展的算力底座。',
    shots: [SHOT_SERVER],
    backgroundUrl: '',
    tags: ['AI 算力', '集群部署', '运维监控']
  },
  {
    id: 'education',
    tabLabel: '某985高校智慧教学平台',
    title: '某985高校智慧教学平台',
    description:
      '建设智慧教学与数字人实训一体化平台，覆盖 AI 课件生成、虚拟讲师与学情分析等模块，支撑大规模在线与混合式教学，形成可推广的标杆示范。',
    shots: [SHOT_SERVER],
    backgroundUrl: '',
    tags: ['智慧教学', '数字人', '学情分析']
  }
];

const cases = computed(() => {
  const fromApi = mapSuccessCasesToShowcases(successCases.value);
  return fromApi.length ? fromApi : FALLBACK_CASES;
});

const activeIndex = ref(0);
const shotIndex = ref(0);

const activeCase = computed(() => cases.value[activeIndex.value] ?? null);

const displayShots = computed(() => {
  const c = activeCase.value;
  if (!c) return [];
  if (c.shots.length) return c.shots;
  if (c.backgroundUrl) return [c.backgroundUrl];
  return [];
});

function selectCase(index: number) {
  activeIndex.value = index;
  shotIndex.value = 0;
}

watch(cases, (list) => {
  if (activeIndex.value >= list.length) {
    activeIndex.value = 0;
    shotIndex.value = 0;
  }
});

watch(activeIndex, () => {
  shotIndex.value = 0;
});

useSeo({
  title: '成功案例',
  description: '海曦成功案例：政企、科研、医疗等多行业场景的 AI 与算力落地实践。'
});
</script>

<style scoped lang="scss">
.cases-page {
  position: relative;
  min-height: 800px;
  padding: 120px 0 88px;
  background: #0a0a0f;
  overflow: hidden;
}

.cases-page::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.35;
  background-image:
    linear-gradient(rgba(0, 149, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 149, 255, 0.04) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: linear-gradient(180deg, #000 0%, transparent 85%);
}

.cases-inner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
}

.cases-tabs {
  margin-bottom: 40px;
}

.cases-tab-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  border-bottom: 2px solid #dce8f0;
}

.cases-tab {
  position: relative;
  flex: 0 1 157px;
  min-width: 0;
  max-width: 157px;
  margin: 0;
  padding: 0 2px 7px;
  border: none;
  background: none;
  cursor: pointer;
  transition:
    color 0.25s ease,
    transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);

  .cases-tab-label {
    display: block;
    max-width: 100%;
    position: relative;
    z-index: 1;
    font-size: 14px;
    font-weight: inherit;
    line-height: 1.4;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: font-weight 0.2s ease;
  }

  &:hover:not(.is-active) {
    color: #0095ff;
    transform: translateY(-2px);

    .cases-tab-label {
      font-weight: 500;
    }
  }

  &.is-active {
    color: #0095ff;

    .cases-tab-label {
      font-weight: 600;
    }
  }

  &.is-active::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -2px;
    height: 2px;
    background: #0095ff;
    border-radius: 1px;
  }

  &.is-active::before {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -24px;
    transform: translateX(-50%);
    border: 8px solid transparent;
    border-top-color: #0095ff;
  }
}

.cases-body {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: 128px;
  align-items: start;
  padding: 32px;
}

.cases-title {
  margin: 0 0 24px;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.35;
}

.cases-desc {
  margin: 0 0 32px;
  font-size: 14px;
  line-height: 1.85;
  text-align: justify;
}

.cases-tags {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
}

.cases-tag {
  padding: 4px 16px;
  border: 1px solid rgba(0, 149, 255, 0.20);
  border-radius: 4px;
  background: #fff;
  font-size: 14px;
  line-height: 1.2;
  color: #0095ff;
  cursor: default;
  transition:
    transform 0.25s cubic-bezier(0.22, 1, 0.36, 1),
    background 0.25s ease,
    border-color 0.25s ease,
    box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(0, 149, 255, 0.45);
    background: rgba(0, 149, 255, 0.06);
    box-shadow: 0 6px 16px rgba(0, 149, 255, 0.12);
  }
}

.cases-visual {
  width: 100%;
}

.cases-carousel {
  position: relative;
  border-radius: 12px;
  background: #dbeafe;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.1);
  transition:
    transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.35s cubic-bezier(0.22, 1, 0.36, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 48px rgba(15, 23, 42, 0.14);

    .cases-hero-img {
      transform: scale(1.04);
    }

    .cases-shadow {
      opacity: 0.9;
      filter: blur(30px);
    }
  }
}

.cases-hero-img {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  z-index: 2;
  position: relative;
  border-radius: 8px;
  transition: transform 0.45s ease;
}

.cases-dots {
  position: absolute;
  right: 16px;
  bottom: 14px;
  display: flex;
  gap: 8px;
  padding: 0;
  margin: 0;
}

.cases-dot {
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  transition:
    background 0.25s ease,
    transform 0.25s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.25s ease;

  &:hover:not(.is-active) {
    background: rgba(255, 255, 255, 0.85);
    transform: scale(1.2);
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.25);
  }

  &.is-active {
    background: #fff;
    transform: scale(1.15);
  }
}

.cases-shadow {
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 400px;
  height: 65px;
  border-radius: 400px;
  background: rgba(0, 149, 255, 0.70);
  filter: blur(26px);
  transition:
    opacity 0.35s ease,
    filter 0.35s ease;
  pointer-events: none;
  z-index: 1;
}

.cases-empty {
  margin: 48px 0 0;
  text-align: center;
  font-size: 15px;
  color: #8c8c8c;
}

.cases-panel-enter-active,
.cases-panel-leave-active {
  transition:
    opacity 0.32s ease,
    transform 0.32s ease;
}

.cases-panel-enter-from {
  opacity: 0;
  transform: translateY(12px);
}

.cases-panel-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.cases-shot-enter-active,
.cases-shot-leave-active {
  transition: opacity 0.28s ease;
}

.cases-shot-enter-from,
.cases-shot-leave-to {
  opacity: 0;
}

.cases-shot-leave-active {
  position: absolute;
  inset: 0;
}

@media (max-width: 1024px) {
  .cases-body {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .cases-visual {
    order: -1;
  }
}

@media (max-width: 640px) {
  .cases-page {
    padding: 100px 0 56px;
  }

  .cases-tab-row {
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
    gap: 20px;
    padding-bottom: 4px;
  }

  .cases-tab-row::-webkit-scrollbar {
    display: none;
  }

  .cases-tab {
    flex: 0 0 auto;
    max-width: 240px;
  }

  .cases-desc {
    text-align: left;
  }

  .cases-carousel:hover {
    transform: none;
  }

  .cases-tag:hover {
    transform: none;
    box-shadow: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cases-tab,
  .cases-tag,
  .cases-carousel,
  .cases-hero-img,
  .cases-dot,
  .cases-shadow {
    transition: none;
  }

  .cases-tab:hover:not(.is-active),
  .cases-carousel:hover,
  .cases-tag:hover,
  .cases-dot:hover:not(.is-active) {
    transform: none;
  }

  .cases-carousel:hover .cases-hero-img {
    transform: none;
  }
}
</style>
